import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, FileType2, Trash2, Star, Upload, Image as ImageIcon } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  name: "",
  title: "ATTESTATO DI FORMAZIONE",
  body_template: "",
  normative_text: "",
  footer_text: "Attestato verificabile online tramite QR code.",
  page2_title: "Approfondimenti sul corso",
  page2_content: "",
  is_default: false,
  accent_color: "#C0522B",
  header_color: "#332F2A",
  border_style: "ornament",
  background_image_url: null,
  show_company_logo: true,
};

const SAMPLE_CTX = {
  numero_attestato: "SG-2026-0042",
  nome_corsista: "Mario Rossi",
  codice_fiscale: "RSSMRA80A01H501U",
  azienda: "Rossi SRL",
  titolo_corso: "Formazione Generale D.Lgs. 81/08",
  ore_corso: "8",
  argomenti: "Rischi generali, DPI, procedure",
  docente: "Dott. Bianchi",
  tutor: "Ing. Verdi",
  soggetto_formatore: "Sicurgarda",
  data_corso: "15/03/2026",
  data_emissione: "18/03/2026",
  data_scadenza: "18/03/2031",
};

function applyPlaceholders(text, ctx) {
  if (!text) return "";
  let out = text;
  for (const [k, v] of Object.entries(ctx)) out = out.replaceAll("{" + k + "}", v);
  return out;
}

export default function TemplatesPage() {
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [defaultBody, setDefaultBody] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [previewLoading, setPreviewLoading] = useState(false);
  const bgRef = useRef(null);

  const handlePreview = useCallback(async () => {
    if (!form.name?.trim() || !form.title?.trim() || !form.body_template?.trim()) {
      toast.error(lang === "it" ? "Compila almeno nome, titolo e corpo prima dell'anteprima" : "Fill at least name, title and body before preview");
      return;
    }
    setPreviewLoading(true);
    try {
      const r = await api.post("/certificate-templates/preview", form, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "it" ? "Errore anteprima" : "Preview error"));
    } finally {
      setPreviewLoading(false);
    }
  }, [form, lang]);

  const load = useCallback(() => api.get("/certificate-templates").then((r) => setList(r.data)), []);
  useEffect(() => {
    load();
    api.get("/certificate-templates/placeholders").then((r) => {
      setPlaceholders(r.data.placeholders);
      setDefaultBody(r.data.default_body);
    });
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, body_template: defaultBody });
    setOpen(true);
  };
  const openEdit = (tpl) => {
    setEditing(tpl.id);
    setForm({
      name: tpl.name, title: tpl.title, body_template: tpl.body_template,
      normative_text: tpl.normative_text || "", footer_text: tpl.footer_text || "",
      page2_title: tpl.page2_title || "Approfondimenti sul corso",
      page2_content: tpl.page2_content || "",
      is_default: !!tpl.is_default,
      accent_color: tpl.accent_color || "#C0522B",
      header_color: tpl.header_color || "#332F2A",
      border_style: tpl.border_style || "ornament",
      background_image_url: tpl.background_image_url || null,
      show_company_logo: tpl.show_company_logo !== false,
    });
    setOpen(true);
  };

  const insertPh = (ph) => setForm((f) => ({ ...f, body_template: (f.body_template || "") + " " + ph }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const savedId = editing
        ? (await api.put(`/certificate-templates/${editing}`, form)).data.id
        : (await api.post("/certificate-templates", form)).data.id;
      toast.success(t("save"));
      setEditing(savedId);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm(lang === "it" ? "Eliminare?" : "Delete?")) return;
    await api.delete(`/certificate-templates/${id}`);
    load();
  };

  const uploadBg = async (file) => {
    if (!editing) {
      toast.error(lang === "it" ? "Salva prima il modello" : "Save the template first");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/certificate-templates/${editing}/background`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, background_image_url: r.data.background_image_url }));
      toast.success(lang === "it" ? "Sfondo caricato" : "Background uploaded");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const removeBg = async () => {
    if (!editing) return;
    await api.delete(`/certificate-templates/${editing}/background`);
    setForm((f) => ({ ...f, background_image_url: null }));
    load();
  };

  const bgSrc = form.background_image_url ? `${BACKEND}${form.background_image_url}` : null;
  const previewTitle = applyPlaceholders(form.title, SAMPLE_CTX);
  const previewBody = applyPlaceholders(form.body_template, SAMPLE_CTX);
  const previewFooter = applyPlaceholders(form.footer_text, SAMPLE_CTX);

  const borderStyleCss = {
    none: "border-transparent",
    single: "border-2",
    double: "border-4 border-double",
    ornament: "border-4 border-double",
  }[form.border_style] || "border-2";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("templates")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{list.length}</p>
        </div>
        <Button onClick={openNew} className="rounded-full btn-lift" data-testid="create-template-button">
          <Plus className="w-4 h-4 mr-1.5" /> {t("add_template")}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((tpl) => (
          <div key={tpl.id} className="bg-card border border-border rounded-lg p-5 card-hover flex flex-col">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md grid place-items-center" style={{ background: (tpl.accent_color || "#C0522B") + "22", color: tpl.accent_color || "#C0522B" }}>
                <FileType2 className="w-5 h-5" />
              </div>
              {tpl.is_default && (
                <span className="text-[10px] uppercase tracking-widest bg-primary/10 text-primary px-2 py-0.5 rounded inline-flex items-center gap-1">
                  <Star className="w-3 h-3" /> Default
                </span>
              )}
            </div>
            <div className="mt-4 font-semibold">{tpl.name}</div>
            <div className="text-xs text-muted-foreground mt-1">{tpl.title}</div>
            <div className="text-xs text-muted-foreground mt-3 line-clamp-3">{tpl.body_template}</div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(tpl)} className="rounded-full">{t("edit")}</Button>
              <Button size="sm" variant="ghost" onClick={() => remove(tpl.id)} className="rounded-full text-destructive">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="text-sm text-muted-foreground">{t("no_data")}</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl">
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("add_template")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-h-[75vh] overflow-y-auto pr-1">
            {/* Form */}
            <form onSubmit={submit} data-testid="template-form" className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("template_name")}</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name-input" /></div>
                <div><Label>{t("template_title")}</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>{t("template_body")}</Label>
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {placeholders.map((ph) => (
                    <button
                      type="button" key={ph} onClick={() => insertPh(ph)}
                      className="text-[10px] font-mono bg-muted hover:bg-primary/10 hover:text-primary border border-border rounded px-1.5 py-0.5 btn-lift"
                      data-testid={`ph-${ph.replace(/[{}]/g, "")}`}
                    >
                      {ph}
                    </button>
                  ))}
                </div>
                <Textarea rows={5} required value={form.body_template} onChange={(e) => setForm({ ...form, body_template: e.target.value })} data-testid="template-body-input" className="font-mono text-sm" />
              </div>

              <div><Label>{t("normative_text")}</Label><Textarea rows={2} value={form.normative_text} onChange={(e) => setForm({ ...form, normative_text: e.target.value })} /></div>
              <div><Label>{t("footer_text")}</Label><Input value={form.footer_text} onChange={(e) => setForm({ ...form, footer_text: e.target.value })} /></div>

              {/* Pagina 2 opzionale (iter 54) */}
              <div className="border-t border-border pt-4 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="text-base font-semibold">{lang === "it" ? "Pagina 2 (facoltativa)" : "Page 2 (optional)"}</Label>
                    <p className="text-xs text-muted-foreground">
                      {lang === "it"
                        ? "Testo libero: programma dettagliato, CV docente, note traduttore, ecc. Se vuoto la seconda pagina non viene generata."
                        : "Free text: detailed program, trainer CV, translator notes, etc. If empty, page 2 is skipped."}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label className="text-xs">{lang === "it" ? "Titolo Pagina 2" : "Page 2 title"}</Label>
                    <Input
                      value={form.page2_title || ""}
                      onChange={(e) => setForm({ ...form, page2_title: e.target.value })}
                      placeholder={lang === "it" ? "Approfondimenti sul corso" : "Course details"}
                      data-testid="template-page2-title-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{lang === "it" ? "Contenuto Pagina 2" : "Page 2 content"}</Label>
                    <Textarea
                      rows={8}
                      value={form.page2_content || ""}
                      onChange={(e) => setForm({ ...form, page2_content: e.target.value })}
                      placeholder={lang === "it"
                        ? "## Programma del corso\n- Modulo 1: Introduzione\n- Modulo 2: Approfondimenti\n\n## Requisiti del docente\n{docente} vanta esperienza pluriennale...\n\n## Note traduzione\n---"
                        : "## Course program\n- Module 1: Introduction\n- Module 2: Deep dive\n\n## Trainer requirements\n{docente} has extensive experience...\n\n---"}
                      className="font-mono text-xs"
                      data-testid="template-page2-content-input"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {lang === "it"
                        ? "Supporta: ## Titolo (sub-heading), - bullet, --- separatore, placeholder es. {docente}"
                        : "Supports: ## heading, - bullet, --- separator, placeholders like {docente}"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{lang === "it" ? "Colore accento" : "Accent color"}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="h-10 w-14 rounded border border-border cursor-pointer" data-testid="accent-color-input" />
                    <Input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="font-mono text-xs" />
                  </div>
                </div>
                <div>
                  <Label>{lang === "it" ? "Colore intestazione" : "Header color"}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={form.header_color} onChange={(e) => setForm({ ...form, header_color: e.target.value })} className="h-10 w-14 rounded border border-border cursor-pointer" />
                    <Input value={form.header_color} onChange={(e) => setForm({ ...form, header_color: e.target.value })} className="font-mono text-xs" />
                  </div>
                </div>
                <div>
                  <Label>{lang === "it" ? "Bordo pagina" : "Page border"}</Label>
                  <Select value={form.border_style} onValueChange={(v) => setForm({ ...form, border_style: v })}>
                    <SelectTrigger data-testid="border-style-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{lang === "it" ? "Nessuno" : "None"}</SelectItem>
                      <SelectItem value="single">{lang === "it" ? "Singolo" : "Single"}</SelectItem>
                      <SelectItem value="double">{lang === "it" ? "Doppio" : "Double"}</SelectItem>
                      <SelectItem value="ornament">{lang === "it" ? "Ornamentale" : "Ornament"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-3 pt-6">
                  <Switch checked={form.show_company_logo} onCheckedChange={(v) => setForm({ ...form, show_company_logo: v })} />
                  <Label>{lang === "it" ? "Mostra logo azienda" : "Show company logo"}</Label>
                </div>
              </div>

              <div>
                <Label>{lang === "it" ? "Immagine di sfondo (filigrana)" : "Background image (watermark)"}</Label>
                <div className="flex gap-2 items-center mt-2">
                  <Button type="button" variant="outline" onClick={() => bgRef.current?.click()} className="rounded-full btn-lift" disabled={!editing} data-testid="bg-upload-button">
                    <Upload className="w-4 h-4 mr-1.5" /> {lang === "it" ? "Carica" : "Upload"}
                  </Button>
                  <input ref={bgRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadBg(e.target.files[0])} />
                  {form.background_image_url && (
                    <Button type="button" variant="ghost" size="sm" onClick={removeBg} className="rounded-full text-destructive">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> {t("delete")}
                    </Button>
                  )}
                  {!editing && (
                    <span className="text-[11px] text-muted-foreground">{lang === "it" ? "Salva prima il modello per abilitare l'upload." : "Save the template first to enable upload."}</span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2 border-t border-border">
                <Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} data-testid="template-default-switch" />
                <Label>{t("set_default")}</Label>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row gap-2">
                <Button type="submit" className="rounded-full btn-lift flex-1" data-testid="template-submit-button">
                  {t("save")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full btn-lift flex-1"
                  onClick={handlePreview}
                  disabled={previewLoading}
                  data-testid="template-preview-pdf-button"
                >
                  {previewLoading ? (lang === "it" ? "Generazione…" : "Generating…") : (lang === "it" ? "Anteprima PDF" : "PDF Preview")}
                </Button>
              </div>
            </form>

            {/* LIVE PREVIEW */}
            <div className="space-y-2">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{lang === "it" ? "Anteprima" : "Live preview"}</div>
              <div
                className={`relative aspect-[1/1.414] w-full bg-white overflow-hidden shadow-md ${borderStyleCss}`}
                style={{ borderColor: form.header_color }}
              >
                {bgSrc && (
                  <img src={bgSrc} alt="" className="absolute inset-6 w-[calc(100%-3rem)] h-[calc(100%-3rem)] object-contain opacity-10 pointer-events-none" />
                )}
                {form.border_style === "ornament" && ["top-2 left-2", "top-2 right-2", "bottom-2 left-2", "bottom-2 right-2"].map((pos) => (
                  <div key={pos} className={`absolute ${pos} w-2.5 h-2.5 rotate-45`} style={{ background: form.header_color }} />
                ))}
                <div className="relative p-8 flex flex-col h-full">
                  <div className="text-white text-center py-3 px-4 rounded" style={{ background: form.header_color, borderBottom: `4px solid ${form.accent_color}` }}>
                    <div className="text-lg font-bold tracking-tight">{previewTitle}</div>
                    <div className="text-[10px] opacity-80 mt-0.5">N. {SAMPLE_CTX.numero_attestato} · {SAMPLE_CTX.soggetto_formatore}</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center justify-center text-center mt-6">
                    <div className="text-[10px] text-neutral-500">Si attesta che</div>
                    <div className="text-2xl font-bold mt-1 text-neutral-900">{SAMPLE_CTX.nome_corsista}</div>
                    <div className="h-px w-40 mt-1" style={{ background: form.accent_color }} />
                    <p className="text-[11px] mt-4 text-neutral-700 leading-relaxed max-w-md">{previewBody}</p>
                    <div className="mt-3 font-bold text-base" style={{ color: form.accent_color }}>{SAMPLE_CTX.titolo_corso}</div>
                    <div className="text-[10px] italic text-neutral-500 mt-1">Corso svolto il {SAMPLE_CTX.data_corso} · {SAMPLE_CTX.ore_corso} ore</div>
                    <p className="text-[9px] italic text-neutral-500 mt-3 max-w-lg">{applyPlaceholders(form.normative_text, SAMPLE_CTX)}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-6 mt-4">
                    <div className="border-t border-neutral-800 pt-1 text-[10px] text-neutral-500">Docente<div className="font-medium text-neutral-800">{SAMPLE_CTX.docente}</div></div>
                    <div className="border-t border-neutral-800 pt-1 text-[10px] text-neutral-500">Tutor<div className="font-medium text-neutral-800">{SAMPLE_CTX.tutor}</div></div>
                  </div>
                  <div className="mt-3 flex items-end justify-between text-[9px] text-neutral-500">
                    <span>{previewFooter}</span>
                    <div className="flex flex-col items-center">
                      <ImageIcon className="w-10 h-10 opacity-30" />
                      <span>QR</span>
                    </div>
                    <span>Rilasciato il {SAMPLE_CTX.data_emissione}</span>
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {lang === "it"
                  ? "Anteprima approssimativa. Il PDF finale mantiene stessi colori, bordo e layout."
                  : "Approximate preview. Final PDF preserves same colors, border, and layout."}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
